import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { EntityCollectionService, EntityServices } from '@ngrx/data';
import { select, Store } from '@ngrx/store';
import clone from 'just-clone';
import moment from 'moment';
import { combineLatest, Observable, ReplaySubject } from 'rxjs';
import { filter, map, takeWhile, tap } from 'rxjs/operators';
import { CompanySetting } from '../../company-setting';
import * as fromActions_CS from '../../company-setting/store/company-setting.actions';
import * as fromReducer_CS from '../../company-setting/store/company-setting.reducer';
import * as fromDataServices from '../../data-services';
import * as fromCommon from '../../shared';
import * as fromShared from '../../shared';
import { LoggingService } from '../../shared/services/logging/logging.service';
import { ICurrentLeaveSummary } from '../ICurrentLeaveSummary';
import { LeaveAdjustment } from '../ILeaveAdjustment';
import { LeaveAdjustmentPresenterComponent } from '../presenter/leave-adjustment-presenter.component';
import * as fromActions_S from '../store/current-leave-summary.actions';
import * as fromReducer_S from '../store/current-leave-summary.reducer';
import * as fromActions from '../store/leave-adjustment.actions';
import * as fromReducer from '../store/leave-adjustment.reducer';
@Component({
    selector: 'ef-leave-adjustment-feature',
    templateUrl: './leave-adjustment-feature.component.html',
    styleUrls: ['./leave-adjustment-feature.component.scss'],
    standalone: false
})
export class LeaveAdjustmentFeatureComponent implements OnInit, OnDestroy {
    process$: Observable<fromShared.Process>;
    @ViewChild(LeaveAdjustmentPresenterComponent, { static: true })
    presenter: LeaveAdjustmentPresenterComponent;
    public adjustments$: Observable<LeaveAdjustment[]>;
    public accrualPeriods$: Observable<
        fromDataServices.AccrualPeriod.AccrualPeriodModel[]
    >;
    public summary$: Observable<ICurrentLeaveSummary[]>;
    public lagDays$: Observable<CompanySetting>;
    selectedError$: Observable<fromShared.Errors.IShowErrors>;
    selectedErrorWithStatus$: Observable<fromCommon.IStatusMessage>;
    active = true;
    ShowErrorSubject = new ReplaySubject<fromShared.Errors.IShowErrors>(1);
    accrualPeriodDS: EntityCollectionService<fromDataServices.AccrualPeriod.AccrualPeriodModel>;

    constructor(
        private log: LoggingService,
        private entityServices: EntityServices,
        private store: Store<fromReducer.State>,
        private store_S: Store<fromReducer_S.State>,
        private store_CS: Store<fromReducer_CS.State>
    ) {
        this.accrualPeriodDS =
            entityServices.getEntityCollectionService<fromDataServices.AccrualPeriod.AccrualPeriodModel>(
                fromDataServices.AccrualPeriod.entityName
            );
    }

    ngOnInit() {
        this.store_CS.dispatch(fromActions_CS.clearCompanySettings());
        this.store.dispatch(fromActions.getAdjustments(null));
        this.store_S.dispatch(fromActions_S.getSummary(null));
        this.store_CS.dispatch(
            fromActions_CS.getCompanySetting({
                SettingType: 'PayPeriodLagInDays'
            })
        );
        this.accrualPeriodDS.getAll();
        this.setupObservables();
        this.setupSubscriptions();
    }

    ngOnDestroy() {
        this.active = false;
        this.store.dispatch(fromActions.clearLeaveAdjustments());
        this.store_S.dispatch(fromActions_S.clearCurrentLeaveSummarys());
    }

    setupObservables() {
        this.process$ = this.store.pipe(select(fromShared.selectCurrent));
        this.adjustments$ = this.store.pipe(
            select(fromReducer.selectAll),
            map(adjustments => clone(adjustments))
        );

        this.summary$ = this.store_S.pipe(
            select(fromReducer_S.selectAll),
            map(summary => clone(summary))
        );

        this.lagDays$ = this.store_CS.pipe(
            select(fromReducer_CS.selectEntity('PayPeriodLagInDays')),
            map(lg => clone(lg))
        );

        this.accrualPeriods$ = this.accrualPeriodDS.entities$;

        this.selectedError$ = this.store.pipe(
            select(fromShared.Errors.getShowErrors),
            tap(selected =>
                this.log.verbose({
                    type: 'LeaveAdjustmentFeatureComponent:selectedError$',
                    selected
                })
            )
        );
        const errors$ = this.store.pipe(select(fromShared.Errors.getErrors));
        this.selectedErrorWithStatus$ = combineLatest([
            this.selectedError$,
            errors$,
            this.adjustments$.pipe(filter(x => !!x))
        ]).pipe(
            tap(([selected, errors]) =>
                this.log.verbose({
                    type: 'LeaveAdjustmentFeatureComponent:errors$',
                    errors,
                    selected
                })
            ),
            map(
                ([selected, errors]) =>
                    errors.filter(
                        e =>
                            e.MsgEntityID === selected.MsgEntityID &&
                            e.MsgProcessID === selected.MsgProcessID
                    )[0]
            ),
            tap(selected =>
                this.log.verbose({
                    type: 'LeaveAdjustmentFeatureComponent:errors$:filtered',
                    selected
                })
            )
        );
    }

    setupSubscriptions() {
        combineLatest([this.process$, this.ShowErrorSubject])
            .pipe(takeWhile(() => this.active))
            .subscribe(([process, event]) => {
                this.store.dispatch(
                    new fromShared.Errors.SetShowErrors({
                        open: event.open,
                        MsgEntityID: event.MsgEntityID,
                        MsgProcessID: process.ProcessID
                    })
                );
            });
    }

    onLoadAdjustments($event) {
        this.log.verbose({
            type: 'LeaveAdjustmentFeatureComponent:onLoadAdjustments'
        });
        if ($event != null) {
            if (moment($event[0]).isValid() && moment($event[1]).isValid()) {
                this.store.dispatch(
                    fromActions.getAdjustments({
                        PPS: $event[0],
                        PPE: $event[1]
                    })
                );
            }
        }
    }

    onSaveAdjustment($event) {
        this.log.verbose({
            type: 'LeaveAdjustmentFeatureComponent:onSaveAdjustment'
        });
        this.store.dispatch(
            fromActions.putAdjustments({ adjustments: [$event] })
        );
    }

    onRecalcAdjustment($event) {
        this.log.verbose({
            type: 'LeaveAdjustmentFeatureComponent:onRecalcAdjustment'
        });
        this.store.dispatch(
            fromActions.putRecalcAdjustments({ adjustments: [$event] })
        );
    }

    onSaveSummary($event) {
        this.log.verbose({
            type: 'LeaveAdjustmentFeatureComponent:onSaveSummary'
        });
        this.store_S.dispatch(fromActions_S.putSummary({ summary: [$event] }));
    }

    onSaveLagDays($event) {
        this.log.verbose({
            type: 'LeaveAdjustmentFeatureComponent:onSaveLagDays'
        });
        this.store_CS.dispatch(
            fromActions_CS.putCompanySettings({ settings: [$event] })
        );
    }

    onSaveAccrualPeriod($event) {
        this.log.verbose({
            type: 'LeaveAdjustmentFeatureComponent:onSaveAccrualPeriod'
        });
        this.accrualPeriodDS.update($event);
    }
    onShowError(event) {
        this.ShowErrorSubject.next(event);
    }
}
